import { useState } from 'react';
import type { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { queryClient } from '../lib/queryClient';
import { api } from '../lib/api';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { useCalendars } from '../hooks/useCalendars';
import type { CalendarWithRole } from '../hooks/useCalendars';
import { useCurrentCalendar } from '../contexts/CurrentCalendarContext';
import { useAllCalendars } from '../contexts/AllCalendarsContext';
import CreateCalendarModal from './modals/CreateCalendarModal';
import { usePWAInstall } from '../hooks/usePWAInstall';
import OfflineStatus from './OfflineStatus';

interface Props {
  children: ReactNode;
}

export default function AppLayout({ children }: Props) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] =
    useState(false);
  const isDesktop = useBreakpoint('sm');
  const { theme } = useCurrentCalendar();

  const { canInstall, install, dismiss } = usePWAInstall();

  const { data: meData } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.me.get(),
  });

  const { data: calendars = [] } = useCalendars();
  const { guestCalendar } = useAllCalendars();

  const allCalendars = guestCalendar
    ? calendars.some(c => c.id === guestCalendar.id)
      ? calendars
      : [...calendars, guestCalendar]
    : calendars;

  function close() {
    setIsOpen(false);
  }

  const sidebarVisible = isDesktop || isOpen;

  return (
    <div
      className="flex min-h-dvh"
      style={{ backgroundColor: theme.surface3 }}
    >
      {/* ── Mobile top header ── */}
      {!isDesktop && (
        <header
          className="fixed top-0 inset-x-0 z-30 h-14 flex items-center gap-3 px-4 border-b"
          style={{
            backgroundColor: theme.surface2,
            borderBottomColor: theme.border,
          }}
        >
          <button
            onClick={() => setIsOpen(o => !o)}
            aria-label="Open menu"
            className="flex flex-col justify-center gap-[5px] w-8 h-8 shrink-0"
          >
            <span className="block h-px w-5 bg-ink-700 transition-all" />
            <span className="block h-px w-5 bg-ink-700 transition-all" />
            <span className="block h-px w-5 bg-ink-700 transition-all" />
          </button>
          <button
            onClick={() => navigate('/calendar')}
            className="font-serif text-lg font-bold tracking-tight text-ink-900"
            aria-label="CommonCal"
          >
            CommonCal
          </button>
        </header>
      )}

      {/* ── Backdrop (mobile only) ── */}
      {!isDesktop && isOpen && (
        <div
          className="fixed inset-0 z-30 bg-ink-900/30"
          onClick={close}
        />
      )}

      {/* ── Sidebar ── */}
      <aside
        style={{
          backgroundColor: theme.surface2,
          borderRightColor: theme.border,
        }}
        className={[
          'flex flex-col border-r shrink-0',
          isDesktop
            ? 'relative w-56 shadow-page'
            : 'fixed inset-y-0 left-0 z-40 w-64 shadow-page transition-transform duration-200',
          !isDesktop && !sidebarVisible
            ? '-translate-x-full'
            : 'translate-x-0',
        ].join(' ')}
      >
        {/* Logo / wordmark + close button */}
        <div
          className="flex items-center justify-between px-5 py-5 border-b"
          style={{ borderBottomColor: theme.border }}
        >
          <button
            onClick={() => navigate('/calendar')}
            className="font-serif text-xl font-bold tracking-tight text-ink-900"
            aria-label="CommonCal"
          >
            CommonCal
          </button>
          {!isDesktop && (
            <button
              onClick={close}
              aria-label="Close menu"
              className="text-ink-400 hover:text-ink-700 transition-colors text-xl leading-none"
            >
              ✕
            </button>
          )}
        </div>

        {/* Calendars heading + New button */}
        <div className="flex items-center justify-between px-4 pt-4 pb-1">
          <span className="font-mono text-xs text-ink-400 uppercase tracking-widest">
            Calendars
          </span>
          <button
            onClick={() => {
              close();
              setShowCreateModal(true);
            }}
            aria-label="New calendar"
            title="New calendar"
            className="w-5 h-5 flex items-center justify-center rounded-sm text-ink-400 hover:text-ink-700 transition-colors text-base leading-none"
            onMouseEnter={e =>
              (e.currentTarget.style.backgroundColor =
                theme.surface3)
            }
            onMouseLeave={e =>
              (e.currentTarget.style.backgroundColor = '')
            }
          >
            +
          </button>
        </div>

        {/* Calendar list */}
        <nav className="flex-1 overflow-y-auto py-1 px-2 space-y-0.5">
          {allCalendars.map((cal: CalendarWithRole) => (
            <div
              key={cal.id}
              className="group relative flex items-center"
            >
              {(cal.role as string) === 'guest' ? (
                <div className="flex-1 flex items-center gap-2.5 px-3 py-2 pr-7 rounded-sm text-sm font-mono truncate text-ink-400 italic select-none">
                  <span
                    className="w-2.5 h-2.5 rounded-full shrink-0 opacity-50"
                    style={{ backgroundColor: cal.color }}
                  />
                  <span className="truncate">
                    {cal.name}
                  </span>
                </div>
              ) : (
                <>
                  <NavLink
                    to={`/calendar/${cal.id}`}
                    onClick={close}
                    className={({ isActive }) =>
                      [
                        'flex-1 flex items-center gap-2.5 px-3 py-2 pr-7 rounded-sm text-sm font-mono truncate transition-colors',
                        isActive
                          ? 'text-ink-900 font-semibold'
                          : 'text-ink-700',
                      ].join(' ')
                    }
                    style={({
                      isActive,
                    }: {
                      isActive: boolean;
                    }) =>
                      isActive
                        ? {
                            backgroundColor: theme.surface3,
                          }
                        : undefined
                    }
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: cal.color }}
                    />
                    <span className="truncate">
                      {cal.name}
                    </span>
                    {cal.is_personal && (
                      <span className="ml-auto text-ink-200 text-xs">
                        you
                      </span>
                    )}
                  </NavLink>
                  <button
                    onClick={e => {
                      e.preventDefault();
                      close();
                      navigate(
                        `/settings?calendar=${cal.id}`
                      );
                    }}
                    aria-label={`Settings for ${cal.name}`}
                    className="absolute right-1 opacity-60 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 w-6 h-6 flex items-center justify-center rounded-sm text-ink-400 hover:text-ink-700 transition-all text-xs"
                  >
                    ⚙
                  </button>
                </>
              )}
            </div>
          ))}
        </nav>

        {/* Bottom: settings + user */}
        <div
          className="border-t px-3 py-3 space-y-1"
          style={{ borderTopColor: theme.border }}
        >
          {canInstall && (
            <div
              className="mx-0 mb-2 px-3 py-2.5 rounded-sm border text-xs font-mono"
              style={{
                backgroundColor: theme.surface3,
                borderColor: theme.border,
              }}
            >
              <p className="text-ink-700 font-semibold mb-1.5">
                Add to home screen
              </p>
              <p className="text-ink-400 mb-2 leading-relaxed">
                Install CommonCal for quick access.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={install}
                  className="btn-primary text-xs py-1 px-2"
                >
                  Install
                </button>
                <button
                  onClick={dismiss}
                  className="btn-ghost text-xs py-1 px-2"
                >
                  Not now
                </button>
              </div>
            </div>
          )}
          <NavLink
            to="/settings"
            onClick={close}
            className={({ isActive }) =>
              [
                'flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm font-mono transition-colors',
                isActive ? 'text-ink-900' : 'text-ink-700',
              ].join(' ')
            }
            style={({ isActive }: { isActive: boolean }) =>
              isActive
                ? { backgroundColor: theme.surface3 }
                : undefined
            }
          >
            Settings
          </NavLink>
          <button
            onClick={async () => {
              await supabase.auth.signOut();
              queryClient.clear();
              navigate('/', { replace: true });
            }}
            className="w-full text-left flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm font-mono text-ink-700 transition-colors"
          >
            Sign out
          </button>
          {meData?.user && (
            <p className="px-3 pt-1 text-xs font-mono text-ink-200 truncate">
              {meData.user.email}
            </p>
          )}
        </div>
      </aside>

      {/* ── Main content ── */}
      <main
        className={[
          'flex-1 flex overflow-hidden',
          !isDesktop ? 'pt-14' : '',
        ].join(' ')}
      >
        {children}
      </main>

      <OfflineStatus />

      {/* ── Modals / Drawers ── */}
      {showCreateModal && (
        <CreateCalendarModal
          onClose={() => setShowCreateModal(false)}
        />
      )}
    </div>
  );
}
